import React, { useRef, useState, useEffect } from 'react';
import { tr } from '../i18n';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { thumbImg } from '../utils/resolveImg';

const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Migrate legacy single-item structure to list
const normalizeSpecial = (special) => {
  if (!special) return null;
  const items = Array.isArray(special.items) && special.items.length
    ? special.items
    : (special.name || special.description || special.price
        ? [{ name: special.name, description: special.description, price: special.price }]
        : []);
  // Honor legacy single `date` field by treating it as both start and end
  const startDate = special.startDate || special.date || '';
  const endDate = special.endDate || special.date || startDate;
  return { ...special, items, startDate, endDate };
};

export const isDailySpecialVisible = (special) => {
  const s = normalizeSpecial(special);
  if (!s || !s.active) return false;
  if (!s.startDate || !s.endDate) return false;
  const today = todayISO();
  if (today < s.startDate || today > s.endDate) return false;
  return (s.items || []).length > 0;
};

const resolveImg = (url) => thumbImg(url, 960);

const DailySpecial = ({ special, lang }) => {
  const s = normalizeSpecial(special);
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const visible = isDailySpecialVisible(s);
  const items = visible ? s.items : [];
  const total = items.length;

  // Keep index in sync when the user scrolls manually (hook must run unconditionally)
  useEffect(() => {
    if (!visible) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;
    let timeout;
    const onScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const children = Array.from(el.children);
        let best = 0;
        let bestDist = Infinity;
        children.forEach((c, i) => {
          const dist = Math.abs(c.offsetLeft - el.offsetLeft - el.scrollLeft);
          if (dist < bestDist) { bestDist = dist; best = i; }
        });
        setIndex(best);
      }, 80);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); clearTimeout(timeout); };
  }, [total, visible]);

  // Auto-scroll every 7 seconds (unless the user is interacting with it)
  useEffect(() => {
    if (!visible || total <= 1 || paused) return undefined;
    const id = setInterval(() => {
      setIndex((cur) => {
        const nextIdx = (cur + 1) % total;
        const el = trackRef.current;
        if (el && el.children[nextIdx]) {
          el.scrollTo({ left: el.children[nextIdx].offsetLeft - el.offsetLeft, behavior: 'smooth' });
        }
        return nextIdx;
      });
    }, 7000);
    return () => clearInterval(id);
  }, [visible, total, paused]);

  if (!visible) return null;

  const scrollTo = (newIndex) => {
    const clamped = (newIndex + total) % total;
    setIndex(clamped);
    const el = trackRef.current;
    if (!el) return;
    const card = el.children[clamped];
    if (card) el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: 'smooth' });
  };

  const prev = () => scrollTo(index - 1);
  const next = () => scrollTo(index + 1);
  return (
    <div className="relative mx-auto max-w-3xl mt-10 mb-2">
      {/* Carousel header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-[#f6c75c] animate-pulse" />
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#c88b17]">
            {lang === 'it' ? 'Oggi in lavagna' : "Today's specials"}
            {total > 1 && <span className="ml-2 text-black/40 tracking-normal">{index + 1} / {total}</span>}
          </p>
        </div>
        {total > 1 && (
          <div className="flex gap-1">
            <button type="button" data-testid="special-prev-btn" onClick={prev} aria-label="Prev" className="w-11 h-11 rounded-full border-[1.5px] border-black bg-white hover:bg-black hover:text-white transition flex items-center justify-center">
              <ChevronLeft size={18} />
            </button>
            <button type="button" data-testid="special-next-btn" onClick={next} aria-label="Next" className="w-11 h-11 rounded-full border-[1.5px] border-black bg-white hover:bg-black hover:text-white transition flex items-center justify-center">
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Track */}
      <div
        ref={trackRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-1 px-1"
        style={{ scrollbarWidth: 'none' }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onPointerDown={() => setPaused(true)}
      >
        {items.map((it, i) => (
          <article
            key={it._id || i}
            className="snap-center flex-shrink-0 w-full relative rounded-[22px] overflow-hidden"
            style={{
              background: 'radial-gradient(circle at 20% 20%, #1b3b2f 0%, #0f2419 70%)',
              boxShadow: '0 18px 40px -14px rgba(0,0,0,0.55), inset 0 0 60px rgba(0,0,0,0.4)',
              border: '6px solid #3a2814',
            }}
          >
            <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(white 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
            <div className="relative flex flex-col md:flex-row gap-5 p-6 md:p-7">
              {it.image && (
                <img
                  src={resolveImg(it.image)}
                  alt={tr(it.name, lang) || 'daily'}
                  className="w-full md:w-40 h-40 md:h-40 object-cover rounded-xl border-2 border-[#3a2814] flex-shrink-0"
                  loading="lazy"
                />
              )}
              <div className="flex-1 min-w-0">
                <h3 translate="no" className="notranslate font-display text-2xl md:text-4xl leading-[1] text-[#f8f1dc]" style={{ textShadow: '0 1px 0 rgba(0,0,0,0.35)' }}>
                  {tr(it.name, lang) || (lang === 'it' ? 'Specialità' : 'Special')}
                </h3>
                {tr(it.description, lang) && (
                  <p translate="no" className="notranslate mt-2 text-[#e9dfc6]/90 text-sm md:text-base leading-snug">
                    {tr(it.description, lang)}
                  </p>
                )}
                {it.price && (
                  <div className="mt-3 inline-flex items-baseline gap-2 bg-[#f6c75c]/10 border border-[#f6c75c]/40 rounded-full px-4 py-1.5">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-[#f6c75c]">{lang === 'it' ? 'Prezzo' : 'Price'}</span>
                    <span className="font-display text-xl text-[#f8f1dc]">€ {it.price}</span>
                  </div>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Dots */}
      {total > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {items.map((it, i) => (
            <button
              key={it._id || `${it.price || ''}-${i}`}
              type="button"
              onClick={() => scrollTo(i)}
              aria-label={`Vai a ${i + 1}`}
              className="py-3 px-1 flex items-center"
            >
              <span className={`block h-1.5 rounded-full transition-all ${i === index ? 'bg-black w-6' : 'bg-black/25 w-1.5'}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DailySpecial;
